import React, { FC, memo, MouseEvent, useCallback, useMemo } from "react";
import { useTheme } from "styled-components";
import { isNil } from "lodash";
import { ITheme } from "@evergis/ui";

import { ContainerTitle, Container, ContainerIconTitle } from "./styled";
import { LegendToggler } from "../../LayerList/styled";
import { VisibilityButton } from "../../../components/Buttons/VisibilityButton";
import { useWidgetContext } from "../../Dashboard/hooks/useWidgetContext";
import { useDashboardLayers } from "../../../hooks/project/useDashboardLayers";
import { useToggle } from "../../../hooks/useToggle";
import { ContainerProps, ContainerTemplate } from "../types";

export const TitleContainer: FC<
  Omit<ContainerProps, "config" | "attributes" | "layerInfo"> & {
    containerId?: string;
    templateName?: string;
    layerNames?: string[];
    fontColor?: string;
    expandable?: boolean;
    expanded?: boolean;
    isVisible?: boolean;
  }
> = memo(
  ({
    containerId,
    templateName,
    layerNames,
    fontColor,
    expandable,
    expanded,
    isVisible,
    elementConfig,
    renderElement,
    type,
  }) => {
    const { palette } = useTheme() as ITheme;
    const { expandContainer, expandedContainers } = useWidgetContext(type);
    const [layersVisibility, toggleVisibility] = useToggle(true);
    const { setDashboardLayer } = useDashboardLayers();

    const { style } = elementConfig || {};
    const isLayers = templateName === ContainerTemplate.Layers;

    const onClick = useCallback(
      (e?: MouseEvent<HTMLButtonElement>) => {
        e?.stopPropagation();

        if (!expandable) return;

        expandContainer(containerId, expanded);
      },
      [containerId, expandContainer, expandable, expanded],
    );

    const onToggleVisibility = useCallback(() => {
      toggleVisibility();
      layerNames?.forEach(layerName => {
        setDashboardLayer({ name: layerName, isVisible: !layersVisibility });
      });
    }, [elementConfig, layerNames, layersVisibility, setDashboardLayer, toggleVisibility]);

    const renderToggler = useMemo(
      () =>
        !!containerId &&
        expandable && (
          <div>
            <LegendToggler
              color={palette.icon}
              toggled={!isNil(expandedContainers?.[containerId]) ? expandedContainers[containerId] : expanded}
              onClick={onClick}
            />
          </div>
        ),
      [containerId, expandable, expanded, expandedContainers, onClick, palette.icon],
    );

    const renderVisibility = useMemo(
      () =>
        isLayers && (
          <div style={{ marginRight: "-0.5rem" }}>
            <VisibilityButton visible={layersVisibility} toggleVisibility={onToggleVisibility} />
          </div>
        ),
      [isLayers, layersVisibility, onToggleVisibility],
    );

    return (
      <Container isTitle={isVisible} style={style}>
        <ContainerTitle>
          <ContainerIconTitle fontColor={fontColor} hasClick={!!onClick}>
            {renderElement({ id: "titleIcon", wrap: false })}
            {renderElement({ id: "title" })}
            {isLayers && renderToggler}
          </ContainerIconTitle>
          {!isLayers && renderToggler}
          {renderVisibility}
        </ContainerTitle>
      </Container>
    );
  },
);
